import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { SeatUpgradeDialog } from '@/components/billing/seat-upgrade-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormFieldError } from '@/components/ui/form-field-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { useAuthStore } from '@/stores/auth-store';
import { useChurchStore } from '@/stores/church-store';
import { formatDateTime } from '@/utils/format';

type ConsoleSystemRole = 'admin' | 'staff' | 'congregant';

interface MembershipUser {
  id: string;
  userId: string;
  systemRole: 'owner' | ConsoleSystemRole;
  staffRoleId: string | null;
  status: string;
  deactivatedAt: string | null;
  pendingInviteId?: string | null;
  user: { id: string; email: string; firstName: string; lastName: string };
}

interface StaffRole {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  permissions: string[];
}

interface PermissionOption {
  code: string;
  module: string;
}

const groupPermissionOptions = (
  permissions: PermissionOption[],
  tPermissions: (key: string) => string,
  moduleLabel: (module: string) => string,
  codes?: Set<string>,
) => {
  const filtered = codes
    ? permissions.filter((permission) => codes.has(permission.code))
    : permissions;

  const map = new Map<string, PermissionOption[]>();
  for (const permission of filtered) {
    const list = map.get(permission.module) ?? [];
    list.push(permission);
    map.set(permission.module, list);
  }

  return [...map.entries()]
    .map(([module, modulePermissions]) => {
      const sorted = [...modulePermissions].sort((left, right) =>
        tPermissions(`code.${left.code}`).localeCompare(tPermissions(`code.${right.code}`)),
      );
      return [module, sorted] as const;
    })
    .sort((left, right) => moduleLabel(left[0]).localeCompare(moduleLabel(right[0])));
};

export const StaffRolesPage = () => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((state) => state.user?.id);
  const actorSystemRole = useChurchStore((state) => state.membership?.systemRole);
  const canChangeSystemRole = actorSystemRole === 'owner';

  const [inviteOpen, setInviteOpen] = useState(false);
  const [previousOpen, setPreviousOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MembershipUser | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<MembershipUser | null>(null);
  const [roleDetail, setRoleDetail] = useState<StaffRole | null>(null);
  const [roleEditor, setRoleEditor] = useState<StaffRole | 'new' | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<MembershipUser | null>(null);
  const [seatUpgradeOpen, setSeatUpgradeOpen] = useState(false);
  const [pendingInvite, setPendingInvite] = useState<{
    email: string;
    systemRole: 'admin' | 'staff';
    staffRoleId?: string | null;
  } | null>(null);

  const currentMembersQuery = useQuery({
    queryKey: ['church-memberships', 'current'],
    queryFn: () =>
      api.get<{ data: MembershipUser[] }>('/churches/memberships', {
        query: { page: 1, pageSize: 100, presence: 'current' },
      }),
  });

  const previousMembersQuery = useQuery({
    queryKey: ['church-memberships', 'previous'],
    queryFn: () =>
      api.get<{ data: MembershipUser[] }>('/churches/memberships', {
        query: { page: 1, pageSize: 100, presence: 'previous' },
      }),
    enabled: previousOpen,
  });

  const rolesQuery = useQuery({
    queryKey: ['staff-roles'],
    queryFn: () => api.get<{ data: StaffRole[] }>('/staff-roles'),
  });

  const permissionsQuery = useQuery({
    queryKey: ['permissions'],
    queryFn: () => api.get<{ data: PermissionOption[] }>('/permissions'),
  });

  const roleNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const role of rolesQuery.data?.data ?? []) map.set(role.id, role.name);
    return map;
  }, [rolesQuery.data]);

  const invite = useMutation({
    mutationFn: (body: {
      email: string;
      systemRole: 'admin' | 'staff';
      staffRoleId?: string | null;
    }) => api.post('/churches/memberships', body),
    onSuccess: async () => {
      toast.success(t('staff.inviteSuccess'));
      setInviteOpen(false);
      setPendingInvite(null);
      await queryClient.invalidateQueries({ queryKey: ['church-memberships'] });
    },
    onError: (err, variables) => {
      if (err instanceof ApiError && err.code === 'SEAT_LIMIT_REACHED') {
        setPendingInvite(variables);
        setSeatUpgradeOpen(true);
        return;
      }
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const deactivate = useMutation({
    mutationFn: (membershipId: string) =>
      api.post(`/churches/memberships/${membershipId}/deactivate`),
    onSuccess: async () => {
      toast.success(t('staff.deactivated'));
      setDeactivateTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['church-memberships'] });
    },
  });

  const reactivate = useMutation({
    mutationFn: (input: {
      membershipId: string;
      systemRole: ConsoleSystemRole;
      staffRoleId: string | null;
    }) =>
      api.post(`/churches/memberships/${input.membershipId}/reactivate`, {
        systemRole: input.systemRole,
        staffRoleId: input.systemRole === 'staff' ? input.staffRoleId : null,
      }),
    onSuccess: async () => {
      toast.success(t('staff.restoreSuccess'));
      setRestoreTarget(null);
      setPreviousOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['church-memberships'] });
    },
  });

  const resendInvite = useMutation({
    mutationFn: (membershipId: string) =>
      api.post(`/churches/memberships/${membershipId}/resend-invite`),
    onSuccess: async () => {
      toast.success(t('staff.resendInviteSuccess'));
      await queryClient.invalidateQueries({ queryKey: ['church-memberships'] });
    },
  });

  const updateAccess = useMutation({
    mutationFn: (input: {
      membershipId: string;
      systemRole: ConsoleSystemRole;
      staffRoleId: string | null;
      changeSystemRole: boolean;
    }) => {
      if (input.changeSystemRole) {
        return api.patch(`/churches/memberships/${input.membershipId}/system-role`, {
          systemRole: input.systemRole,
          staffRoleId: input.systemRole === 'staff' ? input.staffRoleId : null,
        });
      }
      return api.patch(`/churches/memberships/${input.membershipId}/staff-role`, {
        staffRoleId: input.staffRoleId,
      });
    },
    onSuccess: async () => {
      toast.success(t('staff.editSuccess'));
      setEditTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['church-memberships'] });
    },
  });

  const saveRole = useMutation({
    mutationFn: (input: {
      id?: string;
      name: string;
      description: string;
      permissions: string[];
    }) =>
      input.id
        ? api.patch(`/staff-roles/${input.id}`, input)
        : api.post('/staff-roles', input),
    onSuccess: async (_data, variables) => {
      toast.success(variables.id ? t('roles.saved') : t('roles.created'));
      setRoleEditor(null);
      await queryClient.invalidateQueries({ queryKey: ['staff-roles'] });
    },
  });

  const deleteRole = useMutation({
    mutationFn: (staffRoleId: string) => api.delete(`/staff-roles/${staffRoleId}`),
    onSuccess: async () => {
      toast.success(t('roles.deleted'));
      setRoleEditor(null);
      await queryClient.invalidateQueries({ queryKey: ['staff-roles'] });
    },
  });

  const currentMembers = currentMembersQuery.data?.data ?? [];
  const previousMembers = previousMembersQuery.data?.data ?? [];
  const roles = rolesQuery.data?.data ?? [];
  const permissions = permissionsQuery.data?.data ?? [];

  const roleColumns: DataTableColumn<StaffRole>[] = [
    {
      id: 'name',
      header: t('roles.columns.name'),
      cell: (row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.name}</span>
          {row.isDefault ? <Badge>{t('roles.defaultBadge')}</Badge> : null}
        </div>
      ),
    },
    {
      id: 'description',
      header: t('roles.columns.description'),
      cell: (row) => row.description ?? '—',
    },
    {
      id: 'kind',
      header: t('roles.columns.kind'),
      cell: (row) => (row.isDefault ? t('roles.defaultBadge') : '—'),
      hideBelowMd: true,
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('staff.title')}
        description={t('staff.description')}
        tipId="settings.staff.roles"
        headerActions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setRoleEditor('new')}>
              {t('roles.create')}
            </Button>
            <Button variant="outline" onClick={() => setPreviousOpen(true)}>
              {t('staff.previousTitle')}
            </Button>
            <Button onClick={() => setInviteOpen(true)}>{t('staff.invite')}</Button>
          </div>
        }
      />

      <Tabs defaultValue="people">
        <TabsList>
          <TabsTrigger value="people">{t('staff.title')}</TabsTrigger>
          <TabsTrigger value="roles">{t('roles.title')}</TabsTrigger>
        </TabsList>

        <TabsContent value="people" className="mt-4">
          {currentMembers.length === 0 ? (
            <p className="text-body-sm text-fg-muted">{t('staff.empty')}</p>
          ) : (
            <MembersTable
              members={currentMembers}
              roleNameById={roleNameById}
              currentUserId={currentUserId}
              canChangeSystemRole={canChangeSystemRole}
              showStatus
              onEdit={setEditTarget}
              onDeactivate={setDeactivateTarget}
              onResend={(row) => resendInvite.mutate(row.id)}
              resendLoading={resendInvite.isPending}
            />
          )}
        </TabsContent>

        <TabsContent value="roles" className="mt-4">
          <DataTable
            rows={roles}
            columns={roleColumns}
            rowKey={(row) => row.id}
            loading={rolesQuery.isLoading}
            empty={<p className="text-body-sm text-fg-muted">{t('roles.empty')}</p>}
            onRowClick={(row) => setRoleDetail(row)}
          />
        </TabsContent>
      </Tabs>

      <Dialog open={previousOpen} onOpenChange={setPreviousOpen}>
        <DialogContent size="xl" className="overflow-visible" closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{t('staff.previousTitle')}</DialogTitle>
            <p className="text-body-sm text-fg-muted">{t('staff.previousDescription')}</p>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {previousMembersQuery.isLoading ? (
              <p className="text-body-sm text-fg-muted">{tCommon('state.loading')}</p>
            ) : previousMembers.length === 0 ? (
              <p className="text-body-sm text-fg-muted">{t('staff.previousEmpty')}</p>
            ) : (
              <MembersTable
                members={previousMembers}
                roleNameById={roleNameById}
                currentUserId={currentUserId}
                canChangeSystemRole={canChangeSystemRole}
                showStatus
                showRevokedAt
                onRestore={setRestoreTarget}
              />
            )}
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviousOpen(false)}>
              {tCommon('action.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        roles={roles}
        loading={invite.isPending}
        error={
          invite.error instanceof ApiError && invite.error.code !== 'SEAT_LIMIT_REACHED'
            ? tErrors(invite.error.code, invite.error.params)
            : undefined
        }
        onSubmit={(body) => invite.mutate(body)}
      />

      <SeatUpgradeDialog
        open={seatUpgradeOpen}
        onOpenChange={setSeatUpgradeOpen}
        onAdded={() => {
          if (pendingInvite) {
            invite.mutate(pendingInvite);
          }
        }}
      />

      {editTarget ? (
        <EditAccessDialog
          key={editTarget.id}
          member={editTarget}
          roles={roles}
          canChangeSystemRole={canChangeSystemRole}
          loading={updateAccess.isPending}
          error={
            updateAccess.error instanceof ApiError
              ? tErrors(updateAccess.error.code, updateAccess.error.params)
              : undefined
          }
          onClose={() => setEditTarget(null)}
          onSubmit={(body) =>
            updateAccess.mutate({
              membershipId: editTarget.id,
              ...body,
              changeSystemRole: canChangeSystemRole,
            })
          }
        />
      ) : null}

      {restoreTarget ? (
        <RestoreAccessDialog
          key={restoreTarget.id}
          member={restoreTarget}
          roles={roles}
          loading={reactivate.isPending}
          error={
            reactivate.error instanceof ApiError
              ? tErrors(reactivate.error.code, reactivate.error.params)
              : undefined
          }
          onClose={() => setRestoreTarget(null)}
          onSubmit={(body) =>
            reactivate.mutate({
              membershipId: restoreTarget.id,
              ...body,
            })
          }
        />
      ) : null}

      {roleDetail ? (
        <RoleDetailDialog
          key={roleDetail.id}
          role={roleDetail}
          permissions={permissions}
          onClose={() => setRoleDetail(null)}
          onEdit={() => {
            setRoleDetail(null);
            setRoleEditor(roleDetail);
          }}
        />
      ) : null}

      {roleEditor ? (
        <RoleEditorDialog
          key={roleEditor === 'new' ? 'new' : roleEditor.id}
          role={roleEditor === 'new' ? null : roleEditor}
          permissions={permissions}
          loading={saveRole.isPending || deleteRole.isPending}
          error={
            saveRole.error instanceof ApiError
              ? tErrors(saveRole.error.code, saveRole.error.params)
              : deleteRole.error instanceof ApiError
                ? tErrors(deleteRole.error.code, deleteRole.error.params)
                : undefined
          }
          onClose={() => setRoleEditor(null)}
          onSave={(input) => saveRole.mutate(input)}
          onDelete={(id) => deleteRole.mutate(id)}
        />
      ) : null}

      <ConfirmDialog
        open={deactivateTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeactivateTarget(null);
        }}
        title={t('staff.deactivateConfirm.title')}
        description={t('staff.deactivateConfirm.description', {
          name: deactivateTarget
            ? `${deactivateTarget.user.firstName} ${deactivateTarget.user.lastName}`
            : '',
        })}
        confirmLabel={t('staff.deactivateConfirm.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={deactivate.isPending}
        onConfirm={() => {
          if (deactivateTarget) deactivate.mutate(deactivateTarget.id);
        }}
      />
    </div>
  );
};

const MembersTable = ({
  members,
  roleNameById,
  currentUserId,
  canChangeSystemRole,
  showStatus,
  showRevokedAt = false,
  onEdit,
  onDeactivate,
  onRestore,
  onResend,
  resendLoading,
}: {
  members: MembershipUser[];
  roleNameById: Map<string, string>;
  currentUserId: string | undefined;
  canChangeSystemRole: boolean;
  showStatus: boolean;
  showRevokedAt?: boolean;
  onEdit?: (member: MembershipUser) => void;
  onDeactivate?: (member: MembershipUser) => void;
  onRestore?: (member: MembershipUser) => void;
  onResend?: (member: MembershipUser) => void;
  resendLoading?: boolean;
}) => {
  const { t } = useTranslation('admin');
  const { t: tChurches } = useTranslation('churches');
  const showActions = Boolean(onEdit || onDeactivate || onRestore || onResend);

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-left text-body-sm">
        <thead className="bg-surface-sunken text-fg-muted">
          <tr>
            {showRevokedAt ? (
              <th className="px-3 py-2 font-medium">{t('staff.columns.revokedAt')}</th>
            ) : null}
            <th className="px-3 py-2 font-medium">{t('staff.columns.name')}</th>
            <th className="px-3 py-2 font-medium">{t('staff.columns.email')}</th>
            <th className="px-3 py-2 font-medium">{t('staff.columns.role')}</th>
            <th className="px-3 py-2 font-medium">{t('staff.columns.staffRole')}</th>
            {showStatus ? (
              <th className="px-3 py-2 font-medium">{t('staff.columns.status')}</th>
            ) : null}
            {showActions ? <th className="px-3 py-2" /> : null}
          </tr>
        </thead>
        <tbody>
          {members.map((row) => {
            const displayName =
              `${row.user.firstName} ${row.user.lastName}`.trim() || '—';
            const canManage =
              Boolean(onDeactivate) &&
              row.systemRole !== 'owner' &&
              row.userId !== currentUserId &&
              (row.status === 'active' || row.status === 'invited');
            const canEdit =
              Boolean(onEdit) &&
              canManage &&
              row.status === 'active' &&
              (canChangeSystemRole || row.systemRole === 'staff');
            const canRestore = Boolean(onRestore) && row.systemRole !== 'owner';
            const canResend = Boolean(onResend) && row.status === 'invited';
            const statusLabel =
              row.status === 'active' ||
              row.status === 'invited' ||
              row.status === 'suspended' ||
              row.status === 'deactivated'
                ? t(`staff.status.${row.status}`)
                : row.status;

            return (
              <tr key={row.id} className="border-t border-border">
                {showRevokedAt ? (
                  <td className="px-3 py-2 whitespace-nowrap">
                    {row.deactivatedAt ? formatDateTime(row.deactivatedAt) : '—'}
                  </td>
                ) : null}
                <td className="px-3 py-2">{displayName}</td>
                <td className="px-3 py-2">{row.user.email}</td>
                <td className="px-3 py-2">
                  {tChurches(`membership.role.${row.systemRole}`)}
                </td>
                <td className="px-3 py-2">
                  {row.staffRoleId ? (roleNameById.get(row.staffRoleId) ?? '—') : '—'}
                </td>
                {showStatus ? <td className="px-3 py-2">{statusLabel}</td> : null}
                {showActions ? (
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      {canEdit ? (
                        <Button size="sm" variant="ghost" onClick={() => onEdit?.(row)}>
                          {t('staff.edit')}
                        </Button>
                      ) : null}
                      {canResend ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={resendLoading}
                          onClick={() => onResend?.(row)}
                        >
                          {t('staff.resendInvite')}
                        </Button>
                      ) : null}
                      {canManage ? (
                        <Button size="sm" variant="ghost" onClick={() => onDeactivate?.(row)}>
                          {t('staff.deactivate')}
                        </Button>
                      ) : null}
                      {canRestore ? (
                        <Button size="sm" variant="ghost" onClick={() => onRestore?.(row)}>
                          {t('staff.restore')}
                        </Button>
                      ) : null}
                    </div>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const InviteDialog = ({
  open,
  onOpenChange,
  roles,
  loading,
  error,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: StaffRole[];
  loading: boolean;
  error?: string;
  onSubmit: (body: {
    email: string;
    systemRole: 'admin' | 'staff';
    staffRoleId?: string | null;
  }) => void;
}) => {
  const { t } = useTranslation('admin');
  const { t: tChurches } = useTranslation('churches');
  const { t: tCommon } = useTranslation();
  const [email, setEmail] = useState('');
  const [systemRole, setSystemRole] = useState<'admin' | 'staff' | null>('staff');
  const [staffRoleId, setStaffRoleId] = useState<string | null>(null);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setEmail('');
      setSystemRole('staff');
      setStaffRoleId(null);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="overflow-visible" closeLabel={tCommon('action.close')}>
        <DialogHeader>
          <DialogTitle>{t('staff.inviteTitle')}</DialogTitle>
          <p className="text-body-sm text-fg-muted">{t('staff.inviteDescription')}</p>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-email">{t('staff.email')}</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t('staff.systemRole')}</Label>
            <Listbox
              popoverPortal="dialog"
              value={systemRole}
              onChange={setSystemRole}
              options={[
                { value: 'admin', label: tChurches('membership.role.admin') },
                { value: 'staff', label: tChurches('membership.role.staff') },
              ]}
            />
          </div>
          {systemRole === 'staff' ? (
            <div className="flex flex-col gap-1.5">
              <Label>{t('staff.staffRole')}</Label>
              <Listbox
                popoverPortal="dialog"
                value={staffRoleId}
                onChange={setStaffRoleId}
                options={roles.map((role) => ({ value: role.id, label: role.name }))}
              />
            </div>
          ) : null}
          <FormFieldError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {tCommon('action.cancel')}
          </Button>
          <Button
            loading={loading}
            disabled={!email.trim() || !systemRole || (systemRole === 'staff' && !staffRoleId)}
            onClick={() =>
              onSubmit({
                email,
                systemRole: systemRole!,
                staffRoleId: systemRole === 'staff' ? staffRoleId : null,
              })
            }
          >
            {t('staff.inviteSubmit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditAccessDialog = ({
  member,
  roles,
  canChangeSystemRole,
  loading,
  error,
  onClose,
  onSubmit,
}: {
  member: MembershipUser;
  roles: StaffRole[];
  canChangeSystemRole: boolean;
  loading: boolean;
  error?: string;
  onClose: () => void;
  onSubmit: (body: {
    systemRole: ConsoleSystemRole;
    staffRoleId: string | null;
  }) => void;
}) => {
  const { t } = useTranslation('admin');
  const { t: tChurches } = useTranslation('churches');
  const { t: tCommon } = useTranslation();
  const initialRole =
    member.systemRole === 'owner' ? 'staff' : (member.systemRole as ConsoleSystemRole);
  const [systemRole, setSystemRole] = useState<ConsoleSystemRole | null>(initialRole);
  const [staffRoleId, setStaffRoleId] = useState<string | null>(member.staffRoleId);

  const effectiveRole = canChangeSystemRole ? systemRole : (member.systemRole as ConsoleSystemRole);
  const showStaffRole = effectiveRole === 'staff';
  const name = `${member.user.firstName} ${member.user.lastName}`;

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="overflow-visible" closeLabel={tCommon('action.close')}>
        <DialogHeader>
          <DialogTitle>{t('staff.editTitle')}</DialogTitle>
          <p className="text-body-sm text-fg-muted">{t('staff.editDescription')}</p>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>{t('staff.columns.name')}</Label>
            <p className="text-body-sm">{name}</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t('staff.email')}</Label>
            <p className="text-body-sm">{member.user.email}</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t('staff.systemRole')}</Label>
            {canChangeSystemRole ? (
              <Listbox
                popoverPortal="dialog"
                value={systemRole}
                onChange={(value) => {
                  setSystemRole(value);
                  if (value !== 'staff') setStaffRoleId(null);
                }}
                options={[
                  { value: 'admin', label: tChurches('membership.role.admin') },
                  { value: 'staff', label: tChurches('membership.role.staff') },
                  { value: 'congregant', label: tChurches('membership.role.congregant') },
                ]}
              />
            ) : (
              <p className="text-body-sm">{tChurches(`membership.role.${member.systemRole}`)}</p>
            )}
          </div>
          {showStaffRole ? (
            <div className="flex flex-col gap-1.5">
              <Label>{t('staff.staffRole')}</Label>
              <Listbox
                popoverPortal="dialog"
                value={staffRoleId}
                onChange={setStaffRoleId}
                options={roles.map((role) => ({ value: role.id, label: role.name }))}
              />
            </div>
          ) : null}
          <FormFieldError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {tCommon('action.cancel')}
          </Button>
          <Button
            loading={loading}
            disabled={
              !effectiveRole || (effectiveRole === 'staff' && !staffRoleId)
            }
            onClick={() =>
              onSubmit({
                systemRole: effectiveRole!,
                staffRoleId: effectiveRole === 'staff' ? staffRoleId : null,
              })
            }
          >
            {t('staff.editSubmit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RestoreAccessDialog = ({
  member,
  roles,
  loading,
  error,
  onClose,
  onSubmit,
}: {
  member: MembershipUser;
  roles: StaffRole[];
  loading: boolean;
  error?: string;
  onClose: () => void;
  onSubmit: (body: {
    systemRole: ConsoleSystemRole;
    staffRoleId: string | null;
  }) => void;
}) => {
  const { t } = useTranslation('admin');
  const { t: tChurches } = useTranslation('churches');
  const { t: tCommon } = useTranslation();
  const initialRole =
    member.systemRole === 'owner' ? 'staff' : (member.systemRole as ConsoleSystemRole);
  const [systemRole, setSystemRole] = useState<ConsoleSystemRole | null>(initialRole);
  const [staffRoleId, setStaffRoleId] = useState<string | null>(null);
  const name = `${member.user.firstName} ${member.user.lastName}`;

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="overflow-visible" closeLabel={tCommon('action.close')}>
        <DialogHeader>
          <DialogTitle>{t('staff.restoreTitle')}</DialogTitle>
          <p className="text-body-sm text-fg-muted">{t('staff.restoreDescription')}</p>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>{t('staff.columns.name')}</Label>
            <p className="text-body-sm">{name}</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t('staff.email')}</Label>
            <p className="text-body-sm">{member.user.email}</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t('staff.systemRole')}</Label>
            <Listbox
              popoverPortal="dialog"
              value={systemRole}
              onChange={(value) => {
                setSystemRole(value);
                if (value !== 'staff') setStaffRoleId(null);
              }}
              options={[
                { value: 'admin', label: tChurches('membership.role.admin') },
                { value: 'staff', label: tChurches('membership.role.staff') },
                { value: 'congregant', label: tChurches('membership.role.congregant') },
              ]}
            />
          </div>
          {systemRole === 'staff' ? (
            <div className="flex flex-col gap-1.5">
              <Label>{t('staff.staffRole')}</Label>
              <Listbox
                popoverPortal="dialog"
                value={staffRoleId}
                onChange={setStaffRoleId}
                options={roles.map((role) => ({ value: role.id, label: role.name }))}
              />
            </div>
          ) : null}
          <FormFieldError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {tCommon('action.cancel')}
          </Button>
          <Button
            loading={loading}
            disabled={!systemRole || (systemRole === 'staff' && !staffRoleId)}
            onClick={() =>
              onSubmit({
                systemRole: systemRole!,
                staffRoleId: systemRole === 'staff' ? staffRoleId : null,
              })
            }
          >
            {t('staff.restoreSubmit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RoleDetailDialog = ({
  role,
  permissions,
  onClose,
  onEdit,
}: {
  role: StaffRole;
  permissions: PermissionOption[];
  onClose: () => void;
  onEdit: () => void;
}) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation();
  const { t: tModules } = useTranslation('modules');
  const { t: tPermissions } = useTranslation('permissions');

  const rolePermissions = useMemo(() => {
    const byCode = new Map(permissions.map((permission) => [permission.code, permission]));
    return role.permissions.map(
      (code) => byCode.get(code) ?? { code, module: 'other' },
    );
  }, [permissions, role.permissions]);

  const moduleLabel = useCallback(
    (module: string) =>
      module === 'other'
        ? t('roles.otherPermissions')
        : tModules(`catalog.${module}.name`, { defaultValue: module }),
    [t, tModules],
  );

  const grouped = useMemo(
    () => groupPermissionOptions(rolePermissions, tPermissions, moduleLabel),
    [moduleLabel, rolePermissions, tPermissions],
  );

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent size="2xl" className="overflow-visible" closeLabel={tCommon('action.close')}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{role.name}</span>
            {role.isDefault ? <Badge>{t('roles.defaultBadge')}</Badge> : null}
          </DialogTitle>
          {role.description ? (
            <p className="text-body-sm text-fg-muted">{role.description}</p>
          ) : null}
        </DialogHeader>

        <div>
          <p className="text-body-sm font-medium">{t('roles.permissions')}</p>
          {role.permissions.length === 0 ? (
            <p className="mt-2 text-body-sm text-fg-muted">{t('roles.noPermissions')}</p>
          ) : (
            <ScrollArea className="mt-3 h-[min(32rem,60vh)] rounded-lg border border-border p-4">
              <div className="flex flex-col gap-6 pr-3">
                {grouped.map(([module, codes]) => (
                  <section key={module}>
                    <h3 className="mb-3 text-body-sm font-semibold text-fg">
                      {moduleLabel(module)}
                    </h3>
                    <ul className="grid gap-2 sm:grid-cols-2">
                      {codes.map((permission) => (
                        <li
                          key={permission.code}
                          className="rounded-md border border-border/70 bg-surface-sunken/30 p-3"
                        >
                          <p className="text-body-sm font-medium text-fg">
                            {tPermissions(`code.${permission.code}`)}
                          </p>
                          <p className="mt-0.5 text-caption text-fg-muted">
                            {tPermissions(`hint.${permission.code}`)}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {tCommon('action.close')}
          </Button>
          <Button onClick={onEdit}>{t('roles.edit')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RoleEditorDialog = ({
  role,
  permissions,
  loading,
  error,
  onClose,
  onSave,
  onDelete,
}: {
  role: StaffRole | null;
  permissions: PermissionOption[];
  loading: boolean;
  error?: string;
  onClose: () => void;
  onSave: (input: {
    id?: string;
    name: string;
    description: string;
    permissions: string[];
  }) => void;
  onDelete: (id: string) => void;
}) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation();
  const { t: tModules } = useTranslation('modules');
  const { t: tPermissions } = useTranslation('permissions');
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [selected, setSelected] = useState<string[]>(role?.permissions ?? []);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const moduleLabel = useCallback(
    (module: string) => tModules(`catalog.${module}.name`, { defaultValue: module }),
    [tModules],
  );

  const grouped = useMemo(
    () => groupPermissionOptions(permissions, tPermissions, moduleLabel),
    [moduleLabel, permissions, tPermissions],
  );

  return (
    <>
      <Dialog open onOpenChange={(next) => !next && onClose()}>
        <DialogContent size="2xl" className="overflow-visible" closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{role ? t('roles.editTitle') : t('roles.createTitle')}</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="role-name">{t('roles.name')}</Label>
                <Input
                  id="role-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="role-description">{t('roles.descriptionField')}</Label>
                <Input
                  id="role-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </div>
            </div>

            <div>
              <p className="text-body-sm font-medium">{t('roles.permissions')}</p>
              <p className="mt-1 text-body-sm text-fg-muted">{t('roles.permissionsDescription')}</p>
              <ScrollArea className="mt-3 h-[min(32rem,60vh)] rounded-lg border border-border p-4">
                <div className="flex flex-col gap-6 pr-3">
                  {grouped.map(([module, codes]) => (
                    <section key={module}>
                      <h3 className="mb-3 text-body-sm font-semibold text-fg">
                        {moduleLabel(module)}
                      </h3>
                      <ul className="grid gap-2 sm:grid-cols-2">
                        {codes.map((permission) => {
                          const checked = selected.includes(permission.code);
                          const label = tPermissions(`code.${permission.code}`);
                          const hint = tPermissions(`hint.${permission.code}`);
                          return (
                            <li key={permission.code}>
                              <label
                                htmlFor={permission.code}
                                className="flex cursor-pointer items-start gap-3 rounded-md border border-border/70 p-3 transition-colors hover:bg-surface-sunken/60 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring"
                              >
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(value) => {
                                    setSelected((current) =>
                                      value === true
                                        ? [...current, permission.code]
                                        : current.filter((code) => code !== permission.code),
                                    );
                                  }}
                                  id={permission.code}
                                  className="mt-0.5"
                                />
                                <span className="min-w-0">
                                  <span className="block text-body-sm font-medium text-fg">
                                    {label}
                                  </span>
                                  <span className="mt-0.5 block text-caption text-fg-muted">
                                    {hint}
                                  </span>
                                </span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  ))}
                </div>
              </ScrollArea>
            </div>

            <FormFieldError message={error} />
          </div>

          <DialogFooter>
            {role ? (
              <Button
                variant="destructive"
                className="sm:mr-auto"
                onClick={() => setConfirmDelete(true)}
              >
                {t('roles.delete')}
              </Button>
            ) : null}
            <Button variant="outline" onClick={onClose}>
              {tCommon('action.cancel')}
            </Button>
            <Button
              loading={loading}
              disabled={!name.trim()}
              onClick={() =>
                onSave({
                  id: role?.id,
                  name,
                  description,
                  permissions: selected,
                })
              }
            >
              {t('roles.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('roles.deleteConfirm.title')}
        description={t('roles.deleteConfirm.description')}
        confirmLabel={t('roles.deleteConfirm.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={loading}
        onConfirm={() => {
          if (role) onDelete(role.id);
        }}
      />
    </>
  );
};
